import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AfternoonTransferRow, UpdateAfternoonTransferRowPayload } from '../types/bread-transfers.types';

interface AfternoonTransferTableProps {
  locations: AfternoonTransferRow[];
  totals: {
    historicAvgSaleTotal: number;
    totalSalePreviousDay: number;
    totalSalePreviousWeek: number;
    quantityToRequest: number;
    safetyFactor: number;
    requestToBakery: number;
    afternoonSaleHistoric: number;
    afternoonSalePreviousDay: number;
    previousWeekAfternoonSale: number;
    morningTodaySale: number;
    todayAfternoonSale: number;
    saleUntilNow: number;
    currentStock: number;
    suggested: number;
  };
  onUpdateRow: (payload: UpdateAfternoonTransferRowPayload) => void;
  onTotalSuggestedChange?: (total: number) => void;
  disabled?: boolean;
}

export const AfternoonTransferTable: React.FC<AfternoonTransferTableProps> = ({
  locations,
  totals,
  onUpdateRow,
  onTotalSuggestedChange,
  disabled = false
}) => {
  const [suggestedValues, setSuggestedValues] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    if (onTotalSuggestedChange) {
      const total = calculateTotalSuggested();
      onTotalSuggestedChange(total);
    }
  }, [suggestedValues, onTotalSuggestedChange]);
  const handleInputChange = (
    locationId: string,
    field: keyof Omit<AfternoonTransferRow, 'locationId' | 'locationCode' | 'locationName' | 'suggested'>,
    value: string
  ) => {
    const numValue = parseInt(value) || 0;
    onUpdateRow({
      locationId,
      field,
      value: numValue
    });
  };

  const handleSuggestedValueChange = (locationId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setSuggestedValues(prev => ({
      ...prev,
      [locationId]: numValue
    }));
  };

  const getSuggestedValue = (locationId: string, defaultValue: number) => {
    return suggestedValues[locationId] !== undefined ? suggestedValues[locationId] : defaultValue;
  };

  const calculateTotalSuggested = () => {
    return locations.reduce((sum, location) => {
      const value = getSuggestedValue(location.locationId, location.suggested);
      return sum + value;
    }, 0);
  };


  return (
    <Card>
      <CardContent>
        
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LOCAL</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA TARDE HISTÓRICO MAYOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA TARDE DÍA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA SEMANA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA MAÑANA DÍA ANTERIOR
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA HOY TURNO MAÑANA
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VENTA HASTA AL MOMENTO
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    STOCK ACTUAL
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                    SUGERIDO
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {locations.map((location) => (
                  <tr key={location.locationId} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-sm text-gray-900">
                        {location.locationCode} ({location.locationName})
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.afternoonSaleHistoric}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.afternoonSalePreviousDay}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.previousWeekAfternoonSale}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.morningTodaySale}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.todayAfternoonSale}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.saleUntilNow}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold text-sm text-gray-900">
                        {location.currentStock}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center bg-blue-50">
                      <div className="flex justify-center">
                        <Input
                          type="number"
                          value={getSuggestedValue(location.locationId, location.suggested)}
                          onChange={(e) => handleSuggestedValueChange(location.locationId, e.target.value)}
                          className="w-20 text-center font-bold text-blue-700 text-sm h-8"
                          min="0"
                          disabled={disabled}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* First Totals Row - Empty for Input Fields */}
                <tr className="bg-gray-50 border-t-2 border-gray-300">
                  <td className="px-3 py-2 text-gray-900 font-bold">Totales</td>
                  <td className="px-3 py-2 text-center"></td>
                  <td className="px-3 py-2 text-center"></td>
                  <td className="px-3 py-2 text-center"></td>
                  <td className="px-3 py-2 text-center"></td>
                  <td className="px-3 py-2 text-center"></td>
                  <td className="px-3 py-2 text-center"></td>
                  <td className="px-3 py-2 text-center text-gray-900 font-bold">
                    {totals.currentStock}
                  </td>
                  <td className="px-3 py-2 text-center bg-blue-100 text-blue-800 text-lg font-bold">
                    {calculateTotalSuggested()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};